import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { notificationActions } from '../../store/notification/actions';
import { Message } from '../../store/notification/types';

const requiredGPSAaccuracy = 50; // Meters
const watchLocationTimeout = 60000; // Milliseconds

export interface WithLocationProps {
  watchLocation: (
    newLocationCallback: (position: GeolocationPosition) => void,
  ) => () => void;
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function withLocation<T extends WithLocationProps = WithLocationProps>(
  WrappedComponent: React.ComponentType<T>,
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithLocation = (props: Omit<T, keyof WithLocationProps>) => {
    const { t } = useTranslation(['translations']);

    const dispatch = useDispatch();

    const [location, setLocation] = useState<GeolocationPosition>();
    const [locationMessage, setLocationMessage] = useState<Message>();

    const watchIdRef = useRef<number>();
    const timerRef = useRef<NodeJS.Timeout>();
    const locationRef = useRef<GeolocationPosition>();
    const locationMessageRef = useRef<Message>();

    const prevLocationState = usePrevious(location);
    const prevLocationMessageState = usePrevious(locationMessage);

    const clearWatchPosition = () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = undefined;
      }
    };

    const clearWatchTimeout = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };

    const clearMessages = () => {
      if (locationMessageRef.current) {
        dispatch(
          notificationActions.clearMessage(locationMessageRef.current.id),
        );
      }
    };

    const watchLocation = (
      newLocationCallback: (position: GeolocationPosition) => void,
    ) => {
      const handleNewLocation = (position: GeolocationPosition) => {
        if (position.coords.accuracy > requiredGPSAaccuracy) {
          // We skip position if accuracy is below given threshold
          return;
        }

        setLocation(position);
        newLocationCallback(position);
      };

      const handleLocationError = () => {
        const message = Message.error(
          t('registration_page.location_failed'),
          false,
        )
          .withAction(t('registration_page.location_try_again'), () => {
            watchLocation(newLocationCallback);
          })
          .withoutManualClose();
        setLocationMessage(message);
      };

      if (!navigator.geolocation) {
        handleLocationError();
      }

      const message = Message.info(
        t('registration_page.location_looking'),
        false,
      );
      message.manualCloseEnabled = false;
      setLocationMessage(message);

      const options: PositionOptions = {
        enableHighAccuracy: true,
        maximumAge: 0,
      };
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleNewLocation,
        handleLocationError,
        options,
      );

      // We are not able to use the buildin timeout inside watchPosition,
      // because we reject the position if it above a certain threshold,
      // meaning that we have to control when to timeout the location ourself
      timerRef.current = setTimeout(() => {
        clearWatchPosition();

        if (!locationRef.current) {
          handleLocationError();
        }
      }, watchLocationTimeout);

      return () => {
        clearWatchPosition();
        clearWatchTimeout();
        clearMessages();
      };
    };

    useEffect(() => {
      locationRef.current = location;

      if (prevLocationState) {
        // We only want to send notification message if first location
        return;
      }

      if (!location) {
        return;
      }

      const message = Message.success(t('registration_page.location_success'));
      message.hideDuration = 5000;
      setLocationMessage(message);
    }, [location]);

    useEffect(() => {
      locationMessageRef.current = locationMessage;

      if (prevLocationMessageState) {
        dispatch(notificationActions.clearMessage(prevLocationMessageState.id));
      }

      if (!locationMessage) {
        return;
      }

      dispatch(notificationActions.addMessage(locationMessage));
    }, [locationMessage]);

    const locationProps: WithLocationProps = {
      watchLocation: watchLocation,
    };

    return <WrappedComponent {...locationProps} {...(props as T)} />;
  };

  ComponentWithLocation.displayName = `withLocation(${displayName})`;

  return ComponentWithLocation;
}
